/**
 * Best Practice Recommendations Page (DRR-156)
 *
 * Industry-agnostic dashboard displaying best practice recommendations
 * from the tenant's activated Product. Backend-driven: all field definitions,
 * association labels, and intelligence layers come from the GraphQL API.
 *
 * FERP Principles:
 * - Dumb Frontend: renders what backend returns, ZERO hardcoded fields
 * - Mantine v8.x only
 * - Data from backend via recommendations query
 * - Multi-tenant via useTenantId()
 * - Perception-safe framing (DRR-114): "Opportunity" language
 */

import React, { useState } from 'react';
import {
  Container,
  Title,
  Text,
  SimpleGrid,
  Card,
  Group,
  Stack,
  Badge,
  Alert,
  Skeleton,
  Button,
  Textarea,
  Modal,
  Accordion,
} from '@mantine/core';
import {
  IconBulb,
  IconCheck,
  IconX,
  IconAlertCircle,
  IconShieldCheck,
  IconHeartHandshake,
} from '@tabler/icons-react';
import { useQuery, useMutation } from '@apollo/client/react';
import { useTenantId, useAuth } from '@/auth';
import {
  GqlAdoptionStatus,
  useRecommendationsQuery,
  useRecordAdoptionDecisionMutation,
} from '@/generated/graphql';

// ── GraphQL Operations ──

// ── Helper: badge color by status ──

function statusColor(status: string): string {
  switch (status) {
    case 'PENDING': return 'blue';
    case 'ADOPTED': return 'green';
    case 'DISMISSED': return 'gray';
    default: return 'blue';
  }
}

function layerLabel(layer: string): string {
  switch (layer) {
    case 'CONFIG_GAP': return 'Gap Analysis';
    case 'OUTCOME_DRIVEN': return 'Outcome Based';
    case 'PEER_LEARNING': return 'Peer Insight';
    default: return layer;
  }
}

function tagLabel(tag: string): string {
  // "tag:qol:safety" → "Safety"
  const last = tag.split(':').pop() || tag;
  return last.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// ── Page Component ──

export function RecommendationsPage() {
  const tenantId = useTenantId();
  const { tenant } = useAuth();
  const tenantNum = tenant?.num ?? 0;
  const [dismissModal, setDismissModal] = useState<string | null>(null);
  const [dismissReason, setDismissReason] = useState('');

  const { data, loading, error, refetch } = useRecommendationsQuery({
    variables: { tenantNum },
    skip: !tenantId,
  });

  const [recordDecision, { loading: mutating }] = useRecordAdoptionDecisionMutation({
    onCompleted: () => {
      refetch();
      setDismissModal(null);
      setDismissReason('');
    },
  });

  if (!tenantId) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertCircle size={16} />} color="yellow">
          No tenant selected. Please select a tenant to view recommendations.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container size="lg" py="xl">
        <Stack gap="md">
          <Skeleton height={40} />
          <SimpleGrid cols={{ base: 1, sm: 4 }}>
            {[1, 2, 3, 4].map(i => <Skeleton key={i} height={100} />)}
          </SimpleGrid>
          {[1, 2, 3].map(i => <Skeleton key={i} height={120} />)}
        </Stack>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error loading recommendations">
          {error.message}
        </Alert>
      </Container>
    );
  }

  const summary = (data as unknown as { recommendations?: { recommendations?: unknown[]; totalCount?: number; pendingCount?: number; adoptedCount?: number; dismissedCount?: number } } | undefined)?.recommendations;
  const recommendations = summary?.recommendations ?? [];

  const handleAdopt = (practiceRef: string) => {
    recordDecision({
      variables: {
        input: {
          tenantNum,
          practiceRef,
          decision: 'ADOPTED',
          decidedBy: 'current_user', // TODO: from auth context
        },
      },
    });
  };

  const handleDismiss = () => {
    if (!dismissModal || !dismissReason.trim()) return;
    recordDecision({
      variables: {
        input: {
          tenantNum,
          practiceRef: dismissModal,
          decision: 'DISMISSED',
          decidedBy: 'current_user',
          dismissReason: dismissReason.trim(),
        },
      },
    });
  };

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="center">
          <div>
            <Title order={2}>Best Practice Opportunities</Title>
            <Text c="dimmed" size="sm">
              Recommendations from industry associations to improve your operations
            </Text>
          </div>
          <Badge size="lg" variant="light" color="blue">
            {summary?.pendingCount ?? 0} pending review
          </Badge>
        </Group>

        {/* Summary Cards */}
        <SimpleGrid cols={{ base: 2, sm: 4 }}>
          <Card withBorder>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Total</Text>
            <Text size="xl" fw={700}>{summary?.totalCount ?? 0}</Text>
          </Card>
          <Card withBorder>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Pending</Text>
            <Text size="xl" fw={700} c="blue">{summary?.pendingCount ?? 0}</Text>
          </Card>
          <Card withBorder>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Adopted</Text>
            <Text size="xl" fw={700} c="green">{summary?.adoptedCount ?? 0}</Text>
          </Card>
          <Card withBorder>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Dismissed</Text>
            <Text size="xl" fw={700} c="gray">{summary?.dismissedCount ?? 0}</Text>
          </Card>
        </SimpleGrid>

        {/* Empty state */}
        {recommendations.length === 0 && (
          <Alert icon={<IconCheck size={16} />} color="green" title="All caught up!">
            No recommendations at this time. Your facility is aligned with available best practices.
          </Alert>
        )}

        {/* Recommendations List */}
        <Accordion variant="separated">
          {recommendations.map((rec: any) => (
            <Accordion.Item key={rec.practiceRef} value={rec.practiceRef}>
              <Accordion.Control>
                <Group justify="space-between" wrap="nowrap">
                  <Group gap="sm">
                    <IconBulb size={20} color="var(--mantine-color-blue-6)" />
                    <div>
                      <Text fw={600}>{rec.title}</Text>
                      <Text size="xs" c="dimmed">
                        Published by {rec.sourceAssociationLabel || rec.sourceAssociation}
                      </Text>
                    </div>
                  </Group>
                  <Group gap="xs">
                    <Badge size="sm" variant="light">{layerLabel(rec.intelligenceLayer)}</Badge>
                    <Badge size="sm" color={statusColor(rec.adoptionStatus)}>
                      {rec.adoptionStatus}
                    </Badge>
                  </Group>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="sm">
                  <Text size="sm">{rec.description}</Text>

                  {/* Impact tags */}
                  {(rec.improves?.length > 0 || rec.mitigates?.length > 0) && (
                    <Group gap="xs">
                      {rec.improves?.map((tag: string) => (
                        <Badge key={tag} size="xs" color="teal" leftSection={<IconHeartHandshake size={10} />}>
                          Improves: {tagLabel(tag)}
                        </Badge>
                      ))}
                      {rec.mitigates?.map((tag: string) => (
                        <Badge key={tag} size="xs" color="orange" leftSection={<IconShieldCheck size={10} />}>
                          Reduces: {tagLabel(tag)}
                        </Badge>
                      ))}
                    </Group>
                  )}

                  {/* Actions — only for pending */}
                  {rec.adoptionStatus === GqlAdoptionStatus.PENDING && (
                    <Group gap="sm" mt="xs">
                      <Button
                        size="xs"
                        color="green"
                        leftSection={<IconCheck size={14} />}
                        loading={mutating}
                        onClick={() => handleAdopt(rec.practiceRef)}
                      >
                        Adopt Practice
                      </Button>
                      <Button
                        size="xs"
                        variant="light"
                        color="gray"
                        leftSection={<IconX size={14} />}
                        onClick={() => setDismissModal(rec.practiceRef)}
                      >
                        Not Applicable
                      </Button>
                    </Group>
                  )}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      </Stack>

      {/* Dismiss Modal */}
      <Modal
        opened={!!dismissModal}
        onClose={() => { setDismissModal(null); setDismissReason(''); }}
        title="Why is this recommendation not applicable?"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Your feedback helps improve future recommendations.
          </Text>
          <Textarea
            placeholder="e.g., Our facility model doesn't support this approach"
            value={dismissReason}
            onChange={(e) => setDismissReason(e.currentTarget.value)}
            minRows={3}
          />
          <Group justify="flex-end">
            <Button
              variant="light"
              onClick={() => { setDismissModal(null); setDismissReason(''); }}
            >
              Cancel
            </Button>
            <Button
              color="gray"
              disabled={!dismissReason.trim()}
              loading={mutating}
              onClick={handleDismiss}
            >
              Dismiss Recommendation
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

export default RecommendationsPage;
